/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useRef, useState, useEffect, useMemo } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { DialogView } from './DialogView'
import _ from 'lodash'
import { ConfirmDialog } from './ConfirmDialog'
import { putPickingOrderInReview } from '../services/orderServices'
import { NoticeBanner } from './NoticeBanner'
import { useTranslation } from 'react-i18next'
import { resolveReviewRefusalCopy, reviewRefusalLeftBenchIntact } from '../utils/serverErrorCopy'

export const PickingOrderToReviewDialog = ({
  open,
  setOpen,
  tote = '',
  pickingOrderId = 0,
  productsToReviewById = [],
  onSuccess,
  onCancel,
  onBenchMayHaveChanged,
  cancelLabel = 'Cancelar',
  confirmLabel = 'Continuar',
  eraseShippingData = false,
}) => {
  const cancelButtonRef = useRef()
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [openError, setOpenError] = useState(false)
  // Set after a refusal that can follow a bench the server already cleared: these counts are
  // stale from then on, so the dialog stops offering to send them again.
  const [benchMayHaveChanged, setBenchMayHaveChanged] = useState(false)
  const { i18n } = useTranslation()
  const qualityCheckedProducts = useMemo(() => {
    const products = { ...productsToReviewById }
    Object.keys(products).forEach(id => {
      products[id].missing = products[id].quantity - (products[id].reviewedItemsCount || 0)
      products[id].all_scanned = products[id].missing == 0
    })
    setOpenError(false)
    return products
  }, [productsToReviewById])

  const getSku = product => {
    return product.product_codes.find(productCode => productCode.sku).sku
  }

  const product_table_columns = [
    {
      Header: <div className="flex justify-center">SKU</div>,
      accessor: 'sku',
    },
    {
      Header: <div className="flex justify-center">Estado picking</div>,
      accessor: 'all_scanned',
    },
    {
      Header: <div className="flex justify-center">Piezas en el tote</div>,
      accessor: 'reviewedItemsCount',
    },
    {
      Header: <div className="flex justify-center">Piezas faltantes</div>,
      accessor: 'missing',
    },
  ]

  const onConfirmHandler = async () => {
    setConfirmLoading(true)
    try {
      const productsToRevert = Object.values(qualityCheckedProducts).map(product => {
        return {
          product_id: product.id,
          scanned: product.reviewedItemsCount || 0,
          missing: product.missing,
        }
      })
      const response = await putPickingOrderInReview(pickingOrderId, productsToRevert, eraseShippingData)
      onSuccess(response)
    } catch (e) {
      // put_in_review requires every unit of each submitted product to still be
      // PICKED. On a multi-box order the operator discovers the missing pieces AFTER
      // packing some, so those units are PACKED and the request 422s with a
      // developer-facing string:
      //   "PickingOrdersController::put_in_review -> Cannot send from packing to
      //    review order 123. Invalid statuses found for stock units of product_id 45."
      // Since f0c237a6 un-gated this exit from formValid (correctly — this section is
      // FOR missing pieces) that state is reachable, and the raw string was rendered
      // verbatim at the packing bench. Resolving the refusal itself is BUGS-8501;
      // what this does is stop showing a Rails controller name to an operator, while
      // still passing through the server messages that are written for them.
      const serverMessage = e?.response?.data?.error
      // The raw string is what a dev needs when this is reported — keep it in the
      // console even when the operator reads the localized copy.
      console.error('put_in_review refused:', e?.response?.status, serverMessage)
      // Which copy to show is decided in one pure place (resolveReviewRefusalCopy), so
      // it is testable and so the three refusals that are NOT about packed pieces stop
      // borrowing the packed-pieces cause. It also refuses to render a 5xx body: a 500
      // answering { error: "unexpected error" } is a non-empty string that names no
      // class, so it used to reach the bench verbatim, in English.
      const copy = resolveReviewRefusalCopy({ status: e?.response?.status, message: serverMessage, eraseShippingData })
      setOpenError({ status: true, text: copy.key ? i18n.t(copy.key) : copy.text })
      // Refused before the bench was touched: the dialog stays, so the operator can fix the counts.
      // Anything else can follow a bench already cleared, where resending these counts can send a
      // packed piece to the tarja — the screen has to reload what the server now holds first.
      if (onBenchMayHaveChanged && !reviewRefusalLeftBenchIntact({ status: e?.response?.status, code: e?.response?.data?.code })) {
        setBenchMayHaveChanged(true)
        onBenchMayHaveChanged()
      }
    } finally {
      setConfirmLoading(false)
      setOpenConfirmDialog(false)
      //setOpen(false)
    }

    try {
    } catch (e) {}
  }

  const canReversePicking = () => Object.values(qualityCheckedProducts).some(product => product.missing > 0)

  return (
    <DialogView setOpen={setOpen} open={open} className="w-auto">
      <div>
        <div className="mt-3 text-center sm:mt-5">
          <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
            {eraseShippingData
              ? i18n.t('generate_shipment.missing_picking_pieces.send_to_missing_info_title_confirmation', { tote: tote })
              : i18n.t('generate_shipment.missing_picking_pieces.send_to_review_title_confirmation', { tote: tote })}
          </Dialog.Title>
          <div>
            {eraseShippingData
              ? i18n.t('generate_shipment.missing_picking_pieces.send_to_missing_info_question')
              : i18n.t('generate_shipment.missing_picking_pieces.send_to_review_question')}
          </div>
        </div>
      </div>
      <div className="mt-5 sm:mt-6 px-4 overflow-y-auto max-h-96 h-3/5">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="sticky top-0 bg-gray-50">
            <tr>
              {product_table_columns.map((column, index) => (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" key={column.accessor}>
                  {column['Header']}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.values(qualityCheckedProducts).map((product, index) => (
              <tr key={index}>
                {product.quantity > 0 && (
                  <>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                      <span className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mx-1 bg-blue-100 text-blue-800 select-none'}>
                        {getSku(product)}
                      </span>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                      {product.all_scanned ? (
                        <span
                          className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mx-1 bg-green-100 text-green-800 select-none'}
                        >
                          Completo
                        </span>
                      ) : (
                        <span className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mx-1 bg-red-100 text-red-800 select-none'}>
                          Incompleto
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                      <b>{product.reviewedItemsCount}</b>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                      <b>{product.missing}</b>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-center w-2/3 mx-auto mt-5">
        <button
          type="button"
          className="mr-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
          onClick={onCancel}
          ref={cancelButtonRef}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
          onClick={() => setOpenConfirmDialog(true)}
          disabled={!canReversePicking() || benchMayHaveChanged}
        >
          {confirmLabel}
        </button>
      </div>
      <ConfirmDialog
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        title={
          eraseShippingData
            ? i18n.t('generate_shipment.missing_picking_pieces.missing_info_last_confirmation_title')
            : i18n.t('generate_shipment.missing_picking_pieces.in_review_last_confirmation_title')
        }
        description={
          eraseShippingData
            ? i18n.t('generate_shipment.missing_picking_pieces.missing_info_last_confirmation_description')
            : i18n.t('generate_shipment.missing_picking_pieces.in_review_last_confirmation_description')
        }
        confirmLabel="Si"
        onConfirm={() => {
          onConfirmHandler()
        }}
        loading={confirmLoading}
      />
      <div className="mt-1">{openError.status && <NoticeBanner text={openError.text} className="mb-2" />}</div>
    </DialogView>
  )
}
